import React, { ReactElement, useState } from 'react';
import {
  Formik, FormikHelpers, getIn, isString,
} from 'formik';
import {
  Col, Form, Row, Alert, InputGroup,
} from 'react-bootstrap';
import RequiredFieldLabel from '../Partials/RequiredFieldLabel';
import { UserCreationRequest } from '../../../structures/requests/user';
import routes from '../../../utils/routes';
import { UserRegistrationValidation } from './validation';
import { FormValues } from './index';

interface UserRegistrationStepProps {
  onSubmit: (
    values: UserCreationRequest,
    formikHelpers: FormikHelpers<any>,
    setError: () => void
  ) => void,
  footer: (
    isValid: boolean,
    canGoBack: boolean,
    canGoNext: boolean,
    isLast: boolean
  ) => ReactElement,
  hocState: FormValues,
}

const initialValues: UserCreationRequest = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  role: 'user',
};

const UserRegistrationStep: React.FC<UserRegistrationStepProps> = ({
  onSubmit,
  footer,
}): ReactElement => {
  const [state, setState] = useState<{ error: string | null }>({ error: null });

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const setError = (error?: string) => {
    if (error && isString(error)) {
      setState({ ...state, error });
    } else {
      setState({ ...state, error: null });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, formikHelpers) => onSubmit(values, formikHelpers, setError)}
      validationSchema={UserRegistrationValidation}
      validateOnBlur
      validateOnMount
      validateOnChange
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => {
        return (
          <form onSubmit={handleSubmit} className="d-flex flex-column justify-content-between h-100">
            <Row className="p-0 pt-md-3 pl-md-3 pr-md-3 pt-xl-6 pl-xl-6 pr-xl-6">
              <Col xs={12} className="align-content-center">
                <img
                  src="/images/logo.png"
                  alt="Logo"
                  className="img-fluid mb-4 mx-auto d-none d-md-block w-60"
                />
              </Col>
              <Col xs={12} className="mb-2">
                <h2 className="text-blue-500 mb-0">Let's create your account.</h2>
              </Col>
              {state.error ? (
                <Col xs={12}>
                  <Alert variant="danger">
                    {state.error}
                  </Alert>
                </Col>
              ) : null}
              <Col xs={12}>
                <Form.Group controlId="role">
                  <div className="card text-bg-light border border-2px border-dark-subtle">
                    <div className="card-body px-2 py-2">
                      <Form.Label className="font-weight-bold text-muted">
                        <RequiredFieldLabel isInvalid={getIn(touched, 'role') && getIn(errors, 'role')}>
                          I'm interested in:
                        </RequiredFieldLabel>
                      </Form.Label>
                      <div className={`d-flex ${getIn(touched, 'role') && getIn(errors, 'role') ? 'is-invalid' : ''} fs-2`}>
                        <Form.Check
                          value="user"
                          name="role"
                          type="radio"
                          id="role-buyer"
                          label="Buying Puppies"
                          onChange={() => {
                            setFieldValue('role', 'user');
                          }}
                          checked={getIn(values, 'role') === 'user'}
                          className="mr-4"
                        />
                        <Form.Check
                          value="seller"
                          name="role"
                          type="radio"
                          id="role-seller"
                          label="Selling Puppies"
                          onChange={() => {
                            setFieldValue('role', 'seller');
                          }}
                          checked={getIn(values, 'role') === 'seller'}
                        />
                      </div>
                      <Form.Control.Feedback type="invalid">
                        {getIn(touched, 'role') && getIn(errors, 'role')}
                      </Form.Control.Feedback>
                    </div>
                  </div>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="first_name">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={getIn(touched, 'first_name') && getIn(errors, 'first_name')}>
                      First Name
                    </RequiredFieldLabel>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="First Name"
                    value={getIn(values, 'first_name')}
                    isInvalid={getIn(touched, 'first_name') && getIn(errors, 'first_name')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {getIn(errors, 'first_name')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="last_name">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={getIn(touched, 'last_name') && getIn(errors, 'last_name')}>
                      Last Name
                    </RequiredFieldLabel>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Last Name"
                    value={getIn(values, 'last_name')}
                    isInvalid={getIn(touched, 'last_name') && getIn(errors, 'last_name')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {getIn(errors, 'last_name')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="registration-email">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={getIn(touched, 'email') && getIn(errors, 'email')}>
                      Email address
                    </RequiredFieldLabel>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={getIn(values, 'email')}
                    isInvalid={getIn(touched, 'email') && getIn(errors, 'email')}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                  />
                  <Form.Text className="text-muted">
                    We'll never share your email with anyone else.
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {getIn(errors, 'email')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="registration-password">
                  <Form.Label className="font-weight-bold small-txt text-muted">
                    <RequiredFieldLabel isInvalid={getIn(touched, 'password') && getIn(errors, 'password')}>
                      Password
                    </RequiredFieldLabel>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Password"
                      isInvalid={getIn(touched, 'password') && getIn(errors, 'password')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      required
                    />
                    <InputGroup.Text
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer', backgroundColor: 'transparent' }}
                    >
                      <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                    </InputGroup.Text>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    Enter a secure password. We will never ask you for this information.
                  </Form.Text>
                  <Form.Text className="text-muted">
                    Password must be at least 10 characters with a mix
                    of letters, numbers, & symbols.
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {getIn(errors, 'password')}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className="py-sm-2 p-md-3 py-xl-3 px-xl-6">
              <Col xs={12}>
                <small className="text-muted">
                  By signing up to Lancaster Puppies, you confirm that you have read,
                  understood and agreed with Lancaster Puppies
                  {' '}
                  <a target="_blank" href={routes.PRIVACY_POLICY_GET} rel="noreferrer">privacy policy</a>
                  {' '}
                  and
                  {' '}
                  <a target="_blank" href={routes.TERMS_OF_SERVICE_GET} rel="noreferrer">terms of use</a>
                  .
                </small>
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="bg-white">
                {footer(true, false, true, getIn(values, 'role') !== 'seller')}
              </Col>
            </Row>
          </form>
        );
      }}
    </Formik>
  );
};

export default UserRegistrationStep;
